import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardBody,
  CardHeader,
  Heading,
  Text,
  VStack,
  HStack,
  Badge,
  Button,
  Flex,
  Icon,
  useColorModeValue,
  useToast,
  Spinner,
  Alert,
  AlertIcon,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  Progress,
  Avatar,
  Tooltip,
  useDisclosure,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel
} from '@chakra-ui/react';
import {
  MdSearch,
  MdDescription,
  MdCheckCircle,
  MdCancel,
  MdPending,
  MdAccessTime,
  MdComment,
  MdGavel,
  MdVisibility
} from 'react-icons/md';
import { FaFileAlt, FaClipboardCheck } from 'react-icons/fa';
import api from '../../../../services/api';
import DocumentAnalysisPanel from './components/DocumentAnalysisPanel';
import ParecerJuridicoModal from './components/ParecerJuridicoModal';

const MeusPrecatoriosJuridico = () => {
  const [assignments, setAssignments] = useState([]);
  const [filteredAssignments, setFilteredAssignments] = useState([]);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const toast = useToast();
  
  const { isOpen: isAnalysisOpen, onOpen: onAnalysisOpen, onClose: onAnalysisClose } = useDisclosure();
  const { isOpen: isParecerOpen, onOpen: onParecerOpen, onClose: onParecerClose } = useDisclosure();
  
  const cardBg = useColorModeValue('white', 'gray.800');
  const textColor = useColorModeValue('gray.700', 'white');
  const mutedColor = useColorModeValue('gray.600', 'gray.400');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  useEffect(() => {
    fetchMyAssignments();
  }, []);

  useEffect(() => {
    filterAssignments();
  }, [searchTerm, statusFilter, assignments]);

  const fetchMyAssignments = async () => {
    try {
      setLoading(true);
      const response = await api.get('/juridico/my-precatorios');
      console.log('My Precatorios Response:', response);
      // Handle both response structures
      const precatoriosData = response.data || response || {};
      const assignmentsList = Array.isArray(precatoriosData) ? precatoriosData : (precatoriosData.data || []);
      console.log('Setting assignments to:', assignmentsList);
      setAssignments(assignmentsList);
      setFilteredAssignments(assignmentsList);
    } catch (error) {
      console.error('Error fetching assignments:', error);
      toast({
        title: 'Erro ao carregar precatórios',
        description: 'Não foi possível carregar seus precatórios designados',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const filterAssignments = () => {
    let filtered = [...assignments];

    if (searchTerm) {
      filtered = filtered.filter(a =>
        a.numero_precatorio?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        a.nome_beneficiario?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        a.cpf_cnpj?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (statusFilter) {
      filtered = filtered.filter(a => a.overallStatus === statusFilter);
    }

    setFilteredAssignments(filtered);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value || 0);
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('pt-BR');
  };

  const getStatusColor = (status) => {
    const colors = {
      pendente: 'gray',
      em_analise: 'blue',
      aprovado: 'green',
      reprovado: 'red',
      revisao_necessaria: 'orange'
    };
    return colors[status] || 'gray';
  };

  const getStatusLabel = (status) => {
    const labels = {
      pendente: 'Pendente',
      em_analise: 'Em Análise',
      aprovado: 'Aprovado',
      reprovado: 'Reprovado',
      revisao_necessaria: 'Revisão Necessária'
    };
    return labels[status] || status;
  };

  const getStatusIcon = (status) => {
    const icons = {
      pendente: MdPending,
      em_analise: MdAccessTime,
      aprovado: MdCheckCircle,
      reprovado: MdCancel,
      revisao_necessaria: MdDescription
    };
    return icons[status] || MdDescription;
  };

  const handleOpenAnalysis = (assignment) => {
    setSelectedAssignment(assignment);
    onAnalysisOpen();
  };

  const handleOpenParecer = (assignment) => {
    setSelectedAssignment(assignment);
    onParecerOpen();
  };

  const handleAnalysisUpdate = () => {
    fetchMyAssignments();
    onAnalysisClose();
  };

  const handleParecerSubmit = () => {
    fetchMyAssignments();
    onParecerClose();
  };

  if (loading) {
    return (
      <Flex justify="center" align="center" h="400px">
        <Spinner size="xl" color="blue.500" />
      </Flex>
    );
  }

  return (
    <Box>
      <Heading size="lg" mb={6} color={textColor}>
        Meus Precatórios - Análise Jurídica
      </Heading>

      <Card bg={cardBg} shadow="sm">
        <CardHeader>
          <Flex justify="space-between" align="center" flexWrap="wrap" gap={4}>
            <HStack spacing={4}>
              <InputGroup maxW="300px">
                <InputLeftElement pointerEvents="none">
                  <Icon as={MdSearch} color="gray.400" />
                </InputLeftElement>
                <Input
                  placeholder="Buscar precatório..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </InputGroup>
              
              <Select 
                placeholder="Todos os status" 
                maxW="200px"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="pendente">Pendente</option>
                <option value="em_analise">Em Análise</option>
                <option value="aprovado">Aprovado</option>
                <option value="reprovado">Reprovado</option>
                <option value="revisao_necessaria">Revisão</option>
              </Select>
            </HStack>
            
            <Badge colorScheme="blue" fontSize="md" px={3} py={1}>
              {filteredAssignments.length} precatórios
            </Badge>
          </Flex>
        </CardHeader>

        <CardBody>
          {filteredAssignments.length > 0 ? (
            <TableContainer>
              <Table variant="simple" size="sm">
                <Thead>
                  <Tr>
                    <Th>Número</Th>
                    <Th>Beneficiário</Th>
                    <Th>Estado</Th>
                    <Th>Valor</Th>
                    <Th>Progresso</Th>
                    <Th>Status</Th>
                    <Th>Data Designação</Th>
                    <Th>Ações</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {filteredAssignments.map((assignment) => (
                    <Tr key={assignment._id}>
                      <Td>
                        <Text fontWeight="semibold">{assignment.numero_precatorio}</Text>
                      </Td>
                      <Td>
                        <HStack>
                          <Avatar size="xs" name={assignment.nome_beneficiario} />
                          <Box>
                            <Text>{assignment.nome_beneficiario}</Text>
                            <Text fontSize="xs" color={mutedColor}>
                              {assignment.cpf_cnpj}
                            </Text>
                          </Box>
                        </HStack>
                      </Td>
                      <Td>
                        <Badge colorScheme="purple">{assignment.estado}</Badge>
                      </Td>
                      <Td>
                        <Text fontWeight="semibold">
                          {formatCurrency(assignment.valor_atualizado)}
                        </Text>
                      </Td>
                      <Td>
                        <VStack align="stretch" spacing={1}>
                          <Progress 
                            value={assignment.analysisProgress?.percentComplete || 0} 
                            size="xs" 
                            colorScheme={assignment.overallStatus === 'aprovado' ? 'green' : 'blue'}
                            borderRadius="full"
                          />
                          <Text fontSize="xs" color={mutedColor}>
                            {assignment.analysisProgress?.analyzedDocuments || 0}/
                            {assignment.analysisProgress?.totalDocuments || 0} docs
                          </Text>
                        </VStack>
                      </Td>
                      <Td>
                        <Badge colorScheme={getStatusColor(assignment.overallStatus)}>
                          <Icon as={getStatusIcon(assignment.overallStatus)} mr={1} />
                          {getStatusLabel(assignment.overallStatus)}
                        </Badge>
                      </Td>
                      <Td>
                        <Text fontSize="sm">
                          {formatDate(assignment.assignedTo?.assignedAt)}
                        </Text>
                      </Td>
                      <Td>
                        <HStack spacing={2}>
                          <Tooltip label="Analisar Documentos">
                            <Button
                              size="xs"
                              colorScheme="blue"
                              variant="solid"
                              onClick={() => handleOpenAnalysis(assignment)}
                            >
                              <Icon as={FaFileAlt} />
                            </Button>
                          </Tooltip>
                          {assignment.analysisProgress?.percentComplete === 100 && (
                            <Tooltip label="Emitir Parecer">
                              <Button
                                size="xs"
                                colorScheme="green"
                                variant="solid"
                                onClick={() => handleOpenParecer(assignment)}
                              >
                                <Icon as={MdGavel} />
                              </Button>
                            </Tooltip>
                          )}
                        </HStack>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
          ) : (
            <Alert status="info">
              <AlertIcon />
              Nenhum precatório designado para análise
            </Alert>
          )}
        </CardBody>
      </Card>

      {/* Document Analysis Panel */}
      {selectedAssignment && (
        <DocumentAnalysisPanel
          isOpen={isAnalysisOpen}
          onClose={onAnalysisClose}
          assignment={selectedAssignment}
          onUpdate={handleAnalysisUpdate}
        />
      )}

      {/* Parecer Juridico Modal */}
      {selectedAssignment && (
        <ParecerJuridicoModal
          isOpen={isParecerOpen}
          onClose={onParecerClose}
          assignment={selectedAssignment}
          onSubmit={handleParecerSubmit}
        />
      )}
    </Box>
  );
};

export default MeusPrecatoriosJuridico;